﻿import {useSearchParams} from "react-router";
import {useMemo} from "react";

export interface UseBindSearchParamOptions {
  replace?: boolean;
}


/**
 * Binds a single Search Param in the URL to state in react. Use this when you need to update only parameter at a time.
 * If you need to update multiple parameters at a time see {@link useBindSearchQuery}.
 * @param paramName name of the search param in the query url
 * @param defaultValue default value to initiate react state with, removes param from URL if getter is default
 * @param options configuration options
 * @param options.replace If true, replace current history entry instead of pushing new one
 *
 * @see {@link useBindSearchQuery} for multiple param binding.
 */
export const useBindSearchParam = <T extends string>(
  paramName: string,
  defaultValue: T,
  options: UseBindSearchParamOptions = { replace: false }
): [T, (newValue: T) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const getter = useMemo(() => ((searchParams.get(paramName) ?? defaultValue) as T), [searchParams, paramName, defaultValue]);

  // calling the wrapped setting will change searchParams which will trigger the following useEffect to update
  // internal state to match query params. This way the searchParams becomes the single source of truth
  const wrappedSetter: (newValue: T) => void = (newValue) => {
    const currentSearchParamValue = searchParams.get(paramName) as T;
    if (newValue !== currentSearchParamValue) {
      if (newValue && (String(newValue) !== String(defaultValue))) {
        // searchParam and getter mismatch, update searchParam to reflect the value
        setSearchParams(searchParams => {
          searchParams.set(paramName, newValue as string);
          return searchParams;
        }, { replace: options.replace });
      } else {
        // keep URL clean by removing unset/default searchParam value
        if (searchParams.has(paramName)) {
          setSearchParams(searchParams => {
            searchParams.delete(paramName);
            return searchParams;
          }, { replace: options.replace });
        }
      }
    }
  };

  return [getter, wrappedSetter];
};
